// src/components/project/ProjectTableRow.jsx
import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import api from '../../services/api';
import ProjectUsersModal from './ProjectUsersModal';
import {
  updateSoftphoneName,
  toggleProjectStatus,
  deleteProject,
  addUserToProject,
  removeUserFromProject,
  getProjectUsers,
} from '../../services/projectService';

const ProjectTableRow = memo(({ project }) => {
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [softphoneNames, setSoftphoneNames] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);
  // Для percentage_of_analytics_per_operator
  const [editedPercentage, setEditedPercentage] = useState(
    project.percentage_of_analytics_per_operator || 0
  );

  // Синхронизация editedPercentage при изменении project
  useEffect(() => {
    setEditedPercentage(project.percentage_of_analytics_per_operator || 0);
  }, [project.percentage_of_analytics_per_operator]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/projects');
        const data = res.data;
        
        if (Array.isArray(data.all_users)) {
          setAllUsers(data.all_users);
        }
        if (Array.isArray(data.softphone_names)) {
          setSoftphoneNames(data.softphone_names);
        }
        
        if (project.id) {
          const users = await getProjectUsers(project.id);
          setProjectUsers(users || []);
        }
      } catch (err) {
        console.error('Ошибка при загрузке данных для таблицы:', err);
      }
    };
    fetchData();
  }, [project.id]);

  const refreshData = useCallback(async () => {
    try {
      const res = await api.get('/projects');
      const data = res.data;
      
      if (Array.isArray(data.all_users)) {
        setAllUsers(data.all_users);
      }
      if (Array.isArray(data.softphone_names)) {
        setSoftphoneNames(data.softphone_names);
      }
      
      const users = await getProjectUsers(project.id);
      setProjectUsers(users || []);
    } catch (err) {
      console.error('Ошибка при обновлении данных:', err);
    }
  }, [project.id]);

  const handleSoftphoneChange = useCallback(async (value) => {
    const result = await updateSoftphoneName(project.id, value);
    if (result.success) {
      // window.showToast('Имя softphone обновлено');
      refreshData();
    } else {
      window.showToast(result.error);
    }
  }, [project.id, refreshData]);

  const handleAddUser = useCallback(async (userId) => {
    if (!userId) return;

    const result = await addUserToProject(project.id, userId);
    if (result.success) {
      // window.showToast('Пользователь добавлен в проект');
      refreshData();
    } else {
      window.showToast(result.error);
    }
  }, [project.id, refreshData]);

  const handleRemoveUser = useCallback(async (userId) => {
    const result = await removeUserFromProject(project.id, userId);
    if (result.success) {
      // window.showToast('Пользователь удалён из проекта');
      refreshData();
    } else {
      window.showToast(result.error);
    }
  }, [project.id, refreshData]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm('Удалить проект?')) return;

    const result = await deleteProject(project.id);
    if (result.success) {
      // window.showToast('Проект удалён');
      refreshData();
    } else {
      window.showToast(result.error);
    }
  }, [project.id, refreshData]);

  const handleToggleStatus = useCallback(async () => {
    const result = await toggleProjectStatus(project.id);
    if (result.success) {
      // window.showToast('Статус проекта изменён');
      refreshData();
    } else {
      window.showToast(result.error);
    }
  }, [project.id, refreshData]);

  const handleSavePercentage = useCallback(async () => {
    try {
      const result = await api.post(`/projects/${project.id}/percentage`, {
        percentage: editedPercentage,
      });
      if (result.data.success) {
        // window.showToast('Процент аналитики обновлён');
        refreshData();
      } else {
        window.showToast(result.data.error);
      }
    } catch (err) {
      console.error('Ошибка при изменении процента:', err);
    }
  }, [project.id, editedPercentage, refreshData]);

  // Мемоизация частей JSX для оптимизации
  const usersModal = useMemo(() => {
    if (!showUsersModal) return null;

    return (
      <ProjectUsersModal
        project={{ ...project, users: projectUsers }}
        allUsers={allUsers}
        onClose={() => setShowUsersModal(false)}
        onAddUser={(projectId, userId) => handleAddUser(userId)}
        onRemoveUser={(projectId, userId) => handleRemoveUser(userId)}
      />
    );
  }, [showUsersModal, project, projectUsers, allUsers, handleAddUser, handleRemoveUser]);

  const softphoneOptions = useMemo(() => {
    if (!Array.isArray(softphoneNames)) return null;
    
    return (
      <form className="mt-2">
        <select
          className="form-select form-select-sm"
          value={project.softphone_name || ''}
          onChange={(e) => handleSoftphoneChange(e.target.value)}
        >
          <option value="">Не выбран способ получения звонков</option>
          {softphoneNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </form>
    );
  }, [softphoneNames, project.softphone_name, handleSoftphoneChange]);

  const percentageSelect = useMemo(() => {
    return (
      <div className="d-flex align-items-center mt-2 gap-2">
        <label className="form-label mb-0">
          Аналитика на оператора:
        </label>
        <select
          className="form-select form-select-sm"
          style={{ width: '100px' }}
          value={editedPercentage}
          onChange={(e) => setEditedPercentage(Number(e.target.value))}
        >
          {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((percent) => (
            <option key={percent} value={percent / 100}>
              {percent}%
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleSavePercentage}
        >
          Сохранить
        </button>
      </div>
    );
  }, [editedPercentage, handleSavePercentage]);

  return (
    <>
      <tr>
        <td>
          {project.name_text}
          <span className={`badge ms-2 ${project.active ? 'bg-success' : 'bg-danger'}`}>
            {project.active ? 'Активен' : 'Неактивен'}
          </span>
          {softphoneOptions}
        </td>
        <td>
          <button
            type="button"
            className="btn btn-orange btn-sm"
            onClick={() => setShowUsersModal(true)}
          >
            {projectUsers.length > 0 
              ? `Пользователи (${projectUsers.length})` 
              : 'Нет пользователей'}
          </button>
        </td>
        <td>
          <button
            type="button"
            className="btn btn-danger btn-sm me-2"
            onClick={handleDelete}
          >
            Удалить проект
          </button>

          <button
            type="button"
            className={`btn btn-${project.active ? 'warning' : 'success'} btn-sm me-2`}
            onClick={handleToggleStatus}
          >
            {project.active ? 'Деактивировать' : 'Активировать'}
          </button>
          {percentageSelect}
        </td>
      </tr>
      {usersModal}
    </>
  );
});

export default ProjectTableRow;
